import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { X, Settings, ChevronRight, ArrowRight } from 'lucide-react';
import { useAuth } from 'context/AuthContext';
import { Top3Ranking } from 'components/Ranking/Top3Ranking';
import { Top5Ranking } from 'components/Ranking/Top5Ranking';
import { ListRanking } from 'components/Ranking/ListRanking';
import { BannerPodiumPreview } from 'components/Ranking/BannerPodiumPreview';
import { BannerPodiumTreasure } from 'components/Ranking/BannerPodiumTreasure';
import { BannerPodiumMakeup } from 'components/Ranking/BannerPodiumMakeup';
import { BannerPodiumTomb } from 'components/Ranking/BannerPodiumTomb';
import { Ranking, RankingVisualMode, RankingPlayer } from 'types/ranking';
import { 
  getAllRankings, 
  getRankingById,
  getAllLeaderboards,
  getLeaderboardLeaders,
  updateRankingVisuals 
} from 'services/ranking.service';
import RankingSelector from './RankingSelector';
import './styles.scss';

const DisplayMode: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principais
  const [step, setStep] = useState<'selection' | 'display'>('selection');
  const [allRankings, setAllRankings] = useState<Ranking[]>([]);
  const [selectedRankingIds, setSelectedRankingIds] = useState<string[]>([]);
  const [rankingsData, setRankingsData] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  
  // Estados da visualização
  const [showConfig, setShowConfig] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [visualOverrides, setVisualOverrides] = useState<Map<string, RankingVisualMode[]>>(new Map());

  const loadRankings = async () => {
    try {
      setLoading(true);
      const rankings = await getAllRankings();
      setAllRankings(rankings);
    } catch (error) {
      alert('Erro ao carregar rankings. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const leaderToPlayer = React.useCallback((leader: any, maxPoints: number): RankingPlayer => ({
    id: leader.player || leader._id,
    name: leader.name || "Jogador",
    avatar: leader.image,
    fotoDePerfil: leader.image,
    position: leader.position,
    points: leader.total || 0,
    maxPoints: maxPoints,
    level: Math.floor((leader.total || 0) / 200) + 1,
    progress: Math.floor(((leader.total || 0) % 200) / 2),
  }), []);

  const loadRankingData = React.useCallback(async (rankingId: string) => {
    try {
      const ranking = await getRankingById(rankingId);
      const allLeaderboards = await getAllLeaderboards();
      const leaderboard = allLeaderboards.find(
        (lb) => lb.extra?._id === rankingId || lb.title === ranking.title
      );

      if (!leaderboard || !leaderboard._id) {
        return { rankingId, players: [] };
      }

      const leaders = await getLeaderboardLeaders(leaderboard._id);

      if (!leaders || leaders.length === 0) {
        return { rankingId, players: [] };
      }

      const maxPoints = Math.max(...leaders.map((l) => l.total || 0));
      const players: RankingPlayer[] = leaders.map((leader) =>
        leaderToPlayer(leader, maxPoints)
      );
      
      return { rankingId, players };
    } catch (error) {
      return { rankingId, players: [] };
    }
  }, [leaderToPlayer]);

  const handleProceedToDisplay = async () => {
    if (selectedRankingIds.length === 0) {
      alert('Selecione pelo menos um ranking para exibir.');
      return;
    }

    try {
      setLoadingData(true);
      
      // Buscar dados de todos os rankings em paralelo
      const results = await Promise.all(
        selectedRankingIds.map(rankingId => loadRankingData(rankingId))
      );
      
      // Montar o Map com os dados
      const dataMap = new Map();
      results.forEach(({ rankingId, players }) => {
        dataMap.set(rankingId, players);
      });
      
      setRankingsData(dataMap);
      setStep('display');
    } catch (error) {
      alert('Erro ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoadingData(false);
    }
  };

  const handleBackToSelection = () => {
    setStep('selection');
    setShowConfig(false);
  };

  const handleVisualOverride = (rankingId: string, visuals: RankingVisualMode[]) => {
    const newOverrides = new Map(visualOverrides);
    newOverrides.set(rankingId, visuals);
    setVisualOverrides(newOverrides);
  };

  const handleSaveConfiguration = async () => {
    if (visualOverrides.size === 0) {
      alert('Nenhuma alteração para salvar.');
      return;
    }

    try {
      setLoading(true);

      const updatePromises = Array.from(visualOverrides.entries()).map(
        async ([rankingId, visuals]) => {
          const ranking = allRankings.find(r => r._id === rankingId);
          if (ranking) {
            return updateRankingVisuals(rankingId, visuals);
          }
        }
      );

      await Promise.all(updatePromises);
      await loadRankings();
      setVisualOverrides(new Map());
      
      alert('Visualizações salvas com sucesso!');
      setShowConfig(false);
    } catch (error) {
      alert('Erro ao salvar visualizações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const getVisualModesForRanking = (ranking: Ranking): RankingVisualMode[] => {
    if (!ranking._id) return ranking.visuals;
    
    if (visualOverrides.has(ranking._id)) {
      const override = visualOverrides.get(ranking._id);
      return override || ranking.visuals;
    }
    return ranking.visuals;
  };

  const renderRankingVisual = (ranking: Ranking, visualMode: RankingVisualMode) => {
    if (!ranking._id) return null;
    const players = rankingsData.get(ranking._id) || [];
    
    switch (visualMode) {
      case 'podio':
        return (
          <Top3Ranking 
            players={players.slice(0, 3)}
            title={ranking.title}
          />
        );
      case 'lista':
        return (
          <ListRanking 
            players={players}
            title={ranking.title}
          />
        );
      case 'medieval':
        return (
          <div className="rankings-side-by-side">
            <div className="ranking-left">
              <BannerPodiumPreview 
                players={players.slice(0, 3)}
                title="Top 3"
              />
            </div>
            <div className="ranking-right">
              <ListRanking 
                players={players}
                title="Ranking Completo"
              />
            </div>
          </div>
        );
      case 'tesouro':
        return (
          <div className="rankings-side-by-side">
            <div className="ranking-left">
              <BannerPodiumTreasure 
                players={players.slice(0, 3)}
                title="Top 3"
              />
            </div>
            <div className="ranking-right">
              <ListRanking 
                players={players}
                title="Ranking Completo"
              />
            </div>
          </div>
        );
      case 'maquiagem':
        return (
          <div className="rankings-side-by-side">
            <div className="ranking-left">
              <BannerPodiumMakeup 
                players={players.slice(0, 3)}
                title="Top 3"
              />
            </div>
            <div className="ranking-right">
              <ListRanking 
                players={players}
                title="Ranking Completo"
              />
            </div>
          </div>
        );
      case 'tumba':
        return (
          <div className="rankings-side-by-side">
            <div className="ranking-left">
              <BannerPodiumTomb 
                players={players.slice(0, 3)}
                title="Top 3"
              />
            </div>
            <div className="ranking-right">
              <ListRanking 
                players={players}
                title="Ranking Completo"
              />
            </div>
          </div>
        );
      case 'grid':
      case 'privado':
        return (
          <Top5Ranking 
            players={players.slice(0, 5)}
            title={ranking.title}
          />
        );
      default:
        return null;
    }
  };

  const renderRanking = (ranking: Ranking) => {
    if (!ranking._id) return null;
    
    const rankingId = ranking._id;
    const visuals = getVisualModesForRanking(ranking);
    const isConfigMode = showConfig;
    const allVisualModes: RankingVisualMode[] = ['podio', 'lista', 'medieval', 'tesouro', 'maquiagem', 'tumba', 'grid', 'privado'];

    const handleToggleVisual = (visual: RankingVisualMode) => {
      if (visual === 'privado') {
        if (visuals.includes('privado')) {
          handleVisualOverride(rankingId, ['podio']);
          return;
        }
        handleVisualOverride(rankingId, ['privado']);
        return;
      }

      if (visuals.includes('privado')) {
        alert('Para selecionar outro modo, desmarque o modo Privado primeiro.');
        return;
      }

      const newVisuals = visuals.includes(visual)
        ? visuals.filter(v => v !== visual)
        : [...visuals, visual];
      
      if (newVisuals.length > 0 && newVisuals.length <= 3) {
        handleVisualOverride(rankingId, newVisuals);
      }
    };

    return (
      <div key={rankingId} className="ranking-display-item">
        {isConfigMode && (
          <div className="ranking-config-panel">
            <h4 className="config-panel-title">{ranking.title}</h4>
            <p className="config-panel-subtitle">
              Selecione de 1 a 3 modos de visualização
            </p>
            <div className="mode-selector">
              {allVisualModes.map((visual) => {
                const isSelected = visuals.includes(visual);
                const isPrivateMode = visuals.includes('privado');
                
                let isDisabled = false;
                if (visual === 'privado') {
                  isDisabled = false;
                } else {
                  isDisabled = isPrivateMode || (!isSelected && visuals.length >= 3);
                }
                
                return (
                  <button
                    key={visual}
                    className={`mode-option ${isSelected ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && handleToggleVisual(visual)}
                    disabled={isDisabled}
                  >
                    {visual === 'podio' && 'Pódio'}
                    {visual === 'lista' && 'Lista'}
                    {visual === 'medieval' && '🏰 Medieval'}
                    {visual === 'tesouro' && '💎 Tesouro'}
                    {visual === 'maquiagem' && '💄 Maquiagem'}
                    {visual === 'grid' && 'Grid'}
                    {visual === 'privado' && '🔒 Privado'}
                  </button>
                );
              })}
            </div>
            <div className="config-panel-counter">
              {visuals.includes('privado') ? (
                <span className="private-mode-notice">🔒 Modo Privado (exclusivo)</span>
              ) : (
                <span>{visuals.length} / 3 selecionado(s)</span>
              )}
            </div>
          </div>
        )}

        <div className="ranking-wrapper">
          {visuals.map((visual) => (
            <div key={visual} className="ranking-visual-section">
              {renderRankingVisual(ranking, visual)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadRankings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step !== 'display' || selectedRankingIds.length === 0) return;

    const refreshRankingData = async () => {
      try {
        const results = await Promise.all(
          selectedRankingIds.map(rankingId => loadRankingData(rankingId))
        );
        
        const dataMap = new Map();
        results.forEach(({ rankingId, players }) => {
          dataMap.set(rankingId, players);
        });
        
        setRankingsData(dataMap);
      } catch (error) {
        // Silent error
      }
    };

    const intervalId = setInterval(refreshRankingData, 30000);
    return () => clearInterval(intervalId);
  }, [step, selectedRankingIds, loadRankingData]);

  useEffect(() => {
    if (step !== 'display') return;
    
    const checkScroll = () => {
      const hasHorizontalScroll = document.documentElement.scrollWidth > window.innerWidth;
      setShowScrollIndicator(hasHorizontalScroll && selectedRankingIds.length >= 2);
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    if (selectedRankingIds.length >= 2) {
      const timer = setTimeout(() => {
        setShowScrollIndicator(false);
      }, 5000);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', checkScroll);
      };
    }

    return () => window.removeEventListener('resize', checkScroll);
  }, [step, selectedRankingIds.length]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Modo de Exibição - Rankifier</title>
        </Helmet>
        <div className="display-mode-page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Carregando rankings...</p>
          </div>
        </div>
      </>
    );
  }

  if (step === 'selection') {
    return (
      <>
        <Helmet>
          <title>Selecionar Rankings - Rankifier</title>
        </Helmet>
        <div className="display-mode-page selection-step">
          {/* Botão de Fechar */}
          <div className="floating-controls">
            <button
              className="control-btn close-btn"
              onClick={() => navigate('/gestao')}
              title="Voltar"
            >
              <X size={20} />
            </button>
          </div>

          <RankingSelector
            rankings={allRankings}
            selectedRankingIds={selectedRankingIds}
            onSelectionChange={setSelectedRankingIds}
            maxSelection={3}
          />

          {selectedRankingIds.length > 0 && (
            <div className="selection-actions">
              <button
                className="btn-proceed"
                onClick={handleProceedToDisplay}
                disabled={loadingData}
              >
                {loadingData ? 'Carregando...' : (
                  <>
                    Visualizar Rankings
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  // Exibição de rankings
  const selectedRankings = allRankings.filter(r => r._id && selectedRankingIds.includes(r._id));

  return (
    <>
      <Helmet>
        <title>Exibição de Rankings - Rankifier</title>
      </Helmet>

      <div className="display-mode-page">
        {/* Botões de Controle Flutuantes */}
        <div className="floating-controls">
          <button
            className="control-btn settings-btn"
            onClick={() => setShowConfig(!showConfig)}
            title="Configurações de Visualização"
          >
            <Settings size={20} />
          </button>
          <button
            className="control-btn close-btn"
            onClick={handleBackToSelection}
            title="Voltar para Seleção"
          >
            <X size={20} />
          </button>
        </div>

        {/* Painel de Configuração */}
        {showConfig && (
          <div className="config-sidebar">
            <div className="config-section">
              <h3 className="config-title">Configurações de Visualização</h3>
              <p className="config-description">
                Alterne os modos visuais disponíveis para cada ranking
              </p>
            </div>

            <div className="config-divider"></div>

            <div className="config-section">
              <p className="config-description">
                Clique nos botões abaixo de cada ranking para alternar os modos visuais
              </p>
            </div>

            <div className="config-divider"></div>

            <div className="config-actions">
              <button
                className="btn-save-config"
                onClick={handleSaveConfiguration}
                disabled={loading || visualOverrides.size === 0}
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                {loading ? 'Salvando...' : 'Salvar Configurações'}
              </button>
              {visualOverrides.size > 0 && (
                <p className="config-hint">
                  {visualOverrides.size} ranking(s) com alterações
                </p>
              )}
            </div>
          </div>
        )}

        {/* Container dos Rankings */}
        <div className={`rankings-container count-${selectedRankings.length} ${showConfig ? 'with-sidebar' : ''}`}>
          {selectedRankings.map((ranking) => renderRanking(ranking))}
        </div>

        {/* Indicador de Scroll Horizontal */}
        {showScrollIndicator && !showConfig && (
          <div className="scroll-indicator">
            <span>Deslize horizontalmente para ver todos os rankings</span>
            <ChevronRight size={16} />
          </div>
        )}
      </div>
    </>
  );
};

export default DisplayMode;
